import type { ICourse } from "@/api/admin/course";

/**
 * Match a course in the list by whatever key this tenant uses to identify it —
 * erp_unicode, program_name, pok, or id. On IIT KGP pages the lead form's
 * course/interest value is the program_name, so erp_unicode alone won't match.
 */
export function findCourseByKey(
  courses: ICourse[] | undefined | null,
  key: string,
): ICourse | undefined {
  const k = String(key ?? "").trim();
  if (!k || !Array.isArray(courses)) return undefined;
  return courses.find(
    (c) =>
      c?.erp_unicode === k ||
      c?.program_name === k ||
      c?.pok === k ||
      String(c?.id ?? "") === k,
  );
}

/**
 * Brochure to deliver on the download flow. The course the lead actually
 * registered for (selected in the form, or the page/enrollment-bound course) is
 * the source of truth, so resolve its admin-mapped brochure from the courses
 * list first and only fall back to the link passed by the trigger when none is
 * found.
 *
 * This is why a "Download Brochure" CTA on a page with NO global course (e.g.
 * Banner 10 on a generic landing page) still delivers a file: the trigger has
 * no link to pass, but the course picked inside the lead form does.
 */
export function resolveBrochureLink(
  courses: ICourse[] | undefined | null,
  formValues: { interest?: string; course?: string } | undefined | null,
  coursePageErp?: ICourse | null,
  fallback?: string,
): string {
  const key = String(
    formValues?.interest?.trim() ||
      formValues?.course?.trim() ||
      coursePageErp?.erp_unicode ||
      coursePageErp?.program_name ||
      "",
  ).trim();
  const match = findCourseByKey(courses, key);
  const brochure = String(match?.brochure || "").trim();
  if (brochure) return brochure;
  const pageBrochure = String(coursePageErp?.brochure || "").trim();
  if (pageBrochure) return pageBrochure;
  return String(fallback || "").trim();
}
